import { useEffect, useState, useContext, createContext } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Flame, Phone, Mail, MapPin, Menu, X, ClipboardList, Bell, Droplets, ShowerHead, Gauge,
  Lightbulb, Camera, KeyRound, Volume2, CalendarCheck, Landmark, GraduationCap, HeartPulse,
  BedDouble, Building2, Home, ShoppingBag, Briefcase, Warehouse, Factory, Building, Banknote, Store,
  CheckCircle2, Loader2, ChevronDown, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { apiErr } from "@/lib/api";

export async function submitLead(type, payload) {
  try {
    await api.post("/public/leads", { type, ...payload });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: apiErr(e, "Could not submit — please try again or call us directly.") };
  }
}

export async function submitCareer(fields, cvFile) {
  try {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v || ""));
    fd.append("cv", cvFile);
    await api.post("/public/careers", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: apiErr(e, "Could not submit your application — please try again or email us directly.") };
  }
}

/* ---------------- Brand constants ---------------- */
export const COMPANY_NAME = "SecureFlame Fire Systems Private Limited";
export const PHONE_PRIMARY = "+91 70053 28781";
export const PHONE_PRIMARY_TEL = "+917005328781";
export const PHONE_ALT_2 = "+91 82402 06961";
export const COMPANY_EMAIL = "secureflame2026@gmail.com";
export const COMPANY_ADDRESS = "Sujan Pally, Krishnanagar, Colonel Chowmuhani, Agartala, West Tripura – 799001";
export const BUSINESS_HOURS = [
  { d: "Monday – Saturday", h: "9:30 AM – 6:30 PM" },
  { d: "Sunday", h: "Closed" },
];
export const MAP_EMBED_SRC = "https://www.google.com/maps?q=Sujan+Pally,+Krishnanagar,+Colonel+Chowmuhani,+Agartala,+West+Tripura+799001&output=embed";

export const WHATSAPP_NUMBER = "917005328781";
export const DEFAULT_WA_MSG = "Hello SecureFlame, I would like to enquire about fire protection services for my property.";
export const waLink = (text = DEFAULT_WA_MSG) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

export const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.2 5.07 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35z"/>
    <path d="M20.52 3.48A11.87 11.87 0 0 0 12.04 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.3-1.65a11.9 11.9 0 0 0 5.74 1.46h.01c6.55 0 11.88-5.34 11.88-11.9 0-3.18-1.24-6.17-3.41-8.43zM12.05 21.8h-.01a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.27c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.9 6.99c0 5.45-4.44 9.89-9.9 9.89z"/>
  </svg>
);

export const DISTRICTS = ["Agartala", "West Tripura", "Sepahijala", "Gomati", "South Tripura", "Khowai", "Dhalai", "Unakoti", "North Tripura"];

export const TRUST_INDICATORS = [
  "Private Limited Company",
  "GST Registered",
  "MSME / Udyam Registered",
  "Service Across Tripura",
];

export const TRUST_SEGMENTS = ["Government", "Commercial", "Institutional", "Residential", "Industrial"];

export const SERVICES = [
  { slug: "fire-alarm-systems", icon: Bell, title: "Fire Alarm Systems", short: "Fire Alarm", desc: "Design, installation, programming, testing, commissioning and maintenance of conventional and addressable fire alarm systems." },
  { slug: "fire-hydrant-systems", icon: Droplets, title: "Fire Hydrant Systems", short: "Fire Hydrant", desc: "Hydrant piping, landing valves, hose reels, valves, accessories, testing and commissioning." },
  { slug: "sprinkler-systems", icon: ShowerHead, title: "Automatic Sprinkler Systems", short: "Sprinkler", desc: "Complete sprinkler piping, sprinkler heads, valves, supports and pressure testing." },
  { slug: "fire-pump-systems", icon: Gauge, title: "Fire Pump Systems", short: "Fire Pumps", desc: "Main electric fire pumps, jockey pumps, diesel engine pumps, controllers and pump-room installation." },
  { slug: "fire-extinguishers", icon: Flame, title: "Fire Extinguishers", short: "Fire Extinguishers", desc: "Supply, installation, inspection, servicing and refilling support for all extinguisher types." },
  { slug: "emergency-lighting-exit-signage", icon: Lightbulb, title: "Emergency Lighting & Exit Signage", short: "Emergency Lighting", desc: "Non-maintained and maintained emergency lights and illuminated exit signage for safe evacuation." },
  { slug: "cctv-systems", icon: Camera, title: "CCTV Systems", short: "CCTV", desc: "Surveillance camera design, cabling, installation and monitoring setup for premises security." },
  { slug: "access-control-systems", icon: KeyRound, title: "Access Control Systems", short: "Access Control", desc: "Card, biometric and keypad access control for restricted and monitored entry points." },
  { slug: "public-address-systems", icon: Volume2, title: "Public Address Systems", short: "PA Systems", desc: "Voice evacuation and public address systems integrated with fire alarm signalling." },
  { slug: "amc", icon: CalendarCheck, title: "Annual Maintenance Contracts (AMC)", short: "AMC", desc: "Scheduled preventive maintenance, testing and compliance support across all installed systems." },
];

// The 9 services that get dedicated detail pages under /services/:slug (AMC has its own top-level page).
export const SERVICE_PAGES = SERVICES.filter((s) => s.slug !== "amc");

export const SERVICE_AREAS = [
  { slug: "west-tripura", name: "West Tripura", hq: "Agartala" },
  { slug: "sepahijala", name: "Sepahijala", hq: "Bishalgarh" },
  { slug: "gomati", name: "Gomati", hq: "Udaipur" },
  { slug: "south-tripura", name: "South Tripura", hq: "Belonia" },
  { slug: "khowai", name: "Khowai", hq: "Khowai" },
  { slug: "dhalai", name: "Dhalai", hq: "Ambassa" },
  { slug: "unakoti", name: "Unakoti", hq: "Kailashahar" },
  { slug: "north-tripura", name: "North Tripura", hq: "Dharmanagar" },
];

export const INDUSTRIES = [
  { icon: Landmark, title: "Government Buildings" },
  { icon: GraduationCap, title: "Schools & Colleges" },
  { icon: HeartPulse, title: "Hospitals & Healthcare" },
  { icon: BedDouble, title: "Hotels & Hospitality" },
  { icon: Building2, title: "Apartment Complexes" },
  { icon: Home, title: "Residential Buildings" },
  { icon: Store, title: "Commercial Buildings" },
  { icon: ShoppingBag, title: "Shopping Complexes" },
  { icon: Briefcase, title: "Offices" },
  { icon: Banknote, title: "Banks & Financial Institutions" },
  { icon: Warehouse, title: "Warehouses" },
  { icon: Factory, title: "Factories" },
  { icon: Factory, title: "Industrial Facilities" },
  { icon: Building, title: "Institutional Buildings" },
];

/* ---------------- SEO helper (no react-helmet dependency) ---------------- */
// Canonical domain — must match index.html / sitemap.xml / robots.txt exactly (www).
export const SITE_ORIGIN = "https://www.secureflame.in";

// Genuine existing install photographs (from /public/installs, used on Our Work) reused as
// per-service Open Graph images only where a photo of that exact system category exists.
// Services with no matching real photo fall back to the site's default og-image.jpg.
export const SERVICE_OG_IMAGE = {
  "fire-alarm-systems": "/installs/site-4.jpg",
  "fire-hydrant-systems": "/installs/site-3.jpg",
  "sprinkler-systems": "/installs/site-5.jpg",
  "fire-pump-systems": "/installs/site-9.jpg",
  "fire-extinguishers": "/installs/site-1.jpg",
};

/**
 * useSEO — sets per-page title, meta description, canonical, Open Graph, Twitter card,
 * optional noindex, and optional JSON-LD structured data. Restores previous values on unmount
 * so navigating between pages never leaves stale metadata behind.
 * @param {string} title
 * @param {string} description
 * @param {{ image?: string, jsonLd?: object[], noindex?: boolean }} [options]
 */
export function useSEO(title, description, options = {}) {
  const { image, jsonLd, noindex } = options;
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const url = `${SITE_ORIGIN}${window.location.pathname}`;
    const imageUrl = image ? (image.startsWith("http") ? image : `${SITE_ORIGIN}${image}`) : `${SITE_ORIGIN}/og-image.jpg`;
    const setMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      let created = false;
      if (!el) { el = document.createElement(attrs.tag || "meta"); Object.entries(attrs.set).forEach(([k, v]) => el.setAttribute(k, v)); document.head.appendChild(el); created = true; }
      const prev = el.getAttribute(attrs.contentAttr || "content");
      el.setAttribute(attrs.contentAttr || "content", attrs.value);
      return { el, created, prev, contentAttr: attrs.contentAttr || "content" };
    };
    const restore = [
      setMeta('meta[name="description"]', { set: { name: "description" }, value: description }),
      setMeta('meta[property="og:title"]', { set: { property: "og:title" }, value: title }),
      setMeta('meta[property="og:description"]', { set: { property: "og:description" }, value: description }),
      setMeta('meta[property="og:url"]', { set: { property: "og:url" }, value: url }),
      setMeta('meta[property="og:type"]', { set: { property: "og:type" }, value: "website" }),
      setMeta('meta[property="og:image"]', { set: { property: "og:image" }, value: imageUrl }),
      setMeta('meta[name="twitter:card"]', { set: { name: "twitter:card" }, value: "summary_large_image" }),
      setMeta('meta[name="twitter:title"]', { set: { name: "twitter:title" }, value: title }),
      setMeta('meta[name="twitter:description"]', { set: { name: "twitter:description" }, value: description }),
      setMeta('meta[name="twitter:image"]', { set: { name: "twitter:image" }, value: imageUrl }),
      setMeta('link[rel="canonical"]', { tag: "link", set: { rel: "canonical" }, contentAttr: "href", value: url }),
    ];
    const robotsMeta = noindex ? setMeta('meta[name="robots"]', { set: { name: "robots" }, value: "noindex, follow" }) : null;
    const jsonLdEls = (jsonLd || []).map((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-page-jsonld", "true");
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      return script;
    });
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      restore.forEach(({ el, created, prev, contentAttr }) => {
        if (created) el.remove(); else if (prev != null) el.setAttribute(contentAttr, prev);
      });
      if (robotsMeta && robotsMeta.created) robotsMeta.el.remove();
      jsonLdEls.forEach((el) => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, image, noindex, JSON.stringify(jsonLd)]);
}

/* ---------------- Our Work gallery data (16 real install photographs) ---------------- */
export const GALLERY_CATEGORIES = ["Fire Extinguishers", "Fire Hydrant", "Fire Alarm", "Sprinkler", "Fire Pump"];
export const GALLERY_ITEMS = [
  { n: 1, category: "Fire Extinguishers", caption: "Fire Extinguisher Installation" },
  { n: 2, category: "Fire Extinguishers", caption: "Fire Extinguisher Installation" },
  { n: 6, category: "Fire Extinguishers", caption: "Fire Extinguisher Installation" },
  { n: 7, category: "Fire Extinguishers", caption: "Fire Extinguisher Installation" },
  { n: 3, category: "Fire Hydrant", caption: "Fire Hydrant Landing Valve Installation" },
  { n: 8, category: "Fire Hydrant", caption: "Fire Hydrant Hose Reel Installation" },
  { n: 13, category: "Fire Hydrant", caption: "External Fire Hydrant Installation" },
  { n: 14, category: "Fire Hydrant", caption: "Fire Hose Cabinet Installation" },
  { n: 4, category: "Fire Alarm", caption: "Fire Alarm Sounder Installation" },
  { n: 5, category: "Sprinkler", caption: "Sprinkler Head Installation" },
  { n: 11, category: "Sprinkler", caption: "Sprinkler System Valve Installation" },
  { n: 9, category: "Fire Pump", caption: "Fire Pump Installation" },
  { n: 10, category: "Fire Pump", caption: "Fire Pump System Commissioning" },
  { n: 12, category: "Fire Pump", caption: "Fire Pump Pressure Switch Installation" },
  { n: 15, category: "Fire Pump", caption: "Fire Pump Motor Wiring" },
  { n: 16, category: "Fire Pump", caption: "Fire Pump & Water Storage System" },
];

/* ---------------- Quick lead modals (Get A Quote / Free Site Visit — usable from any page) ---------------- */
const LeadModalContext = createContext(null);
export function useLeadModal() {
  return useContext(LeadModalContext);
}

function QuickSiteVisitModal({ open, onClose, context }) {
  const EMPTY = { name: "", phone: "", location: "", building_type: "", service: "" };
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const handleClose = () => { onClose(); setTimeout(() => { setForm(EMPTY); setDone(false); setError(""); }, 200); };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.phone.trim()) { setError("Please enter your name and phone number."); return; }
    setSubmitting(true); setError("");
    const res = await submitLead("site_visit", { ...form, site_address: form.location, ...(context || {}) });
    setSubmitting(false);
    if (res.ok) setDone(true); else setError(res.error);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="quick-sitevisit-modal">
        {done ? (
          <div className="text-center py-8" data-testid="quick-sitevisit-success">
            <CheckCircle2 className="text-primary mx-auto mb-3" size={40} />
            <h3 className="font-heading font-bold text-xl text-secondary">Site visit requested!</h3>
            <p className="mt-2 text-sm text-muted-foreground">Our team will call you shortly to confirm a convenient time.</p>
            <Button onClick={handleClose} className="mt-5 rounded-sm bg-primary hover:bg-primary/90" data-testid="quick-sitevisit-close-btn">Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-secondary font-heading">Request a Free Site Visit</DialogTitle>
              <DialogDescription>Tell us a little about your site — our team will call to schedule a visit.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="quick-sitevisit-form">
              <div><Label htmlFor="qsv-name">Name *</Label><Input id="qsv-name" value={form.name} onChange={set("name")} className="mt-1.5 rounded-sm" data-testid="quick-sitevisit-name" /></div>
              <div><Label htmlFor="qsv-phone">Phone Number *</Label><Input id="qsv-phone" type="tel" value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-sm" data-testid="quick-sitevisit-phone" /></div>
              <div><Label htmlFor="qsv-location">Location</Label><Input id="qsv-location" value={form.location} onChange={set("location")} className="mt-1.5 rounded-sm" data-testid="quick-sitevisit-location" /></div>
              <div><Label htmlFor="qsv-building">Building / Property Type</Label><Input id="qsv-building" placeholder="e.g. Hospital, Apartment, Office" value={form.building_type} onChange={set("building_type")} className="mt-1.5 rounded-sm" data-testid="quick-sitevisit-building" /></div>
              <div><Label htmlFor="qsv-service">Service Required</Label><Input id="qsv-service" placeholder="e.g. Fire Alarm, AMC" value={form.service} onChange={set("service")} className="mt-1.5 rounded-sm" data-testid="quick-sitevisit-service" /></div>
              {error && <p className="text-sm text-primary" data-testid="quick-sitevisit-error">{error}</p>}
              <Button type="submit" disabled={submitting} className="w-full rounded-sm bg-primary hover:bg-primary/90 font-semibold gap-2" data-testid="quick-sitevisit-submit-btn">
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : "REQUEST FREE SITE VISIT"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function QuickQuoteModal({ open, onClose, context }) {
  const EMPTY = { name: "", organization: "", phone: "", email: "", location: "", building_type: "", service: "", message: "" };
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const handleClose = () => { onClose(); setTimeout(() => { setForm(EMPTY); setDone(false); setError(""); }, 200); };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.phone.trim()) { setError("Please enter your name and phone number."); return; }
    setSubmitting(true); setError("");
    const res = await submitLead("quote", { ...form, ...(context || {}) });
    setSubmitting(false);
    if (res.ok) setDone(true); else setError(res.error);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="rounded-sm max-w-lg max-h-[85vh] overflow-y-auto" data-testid="quick-quote-modal">
        {done ? (
          <div className="text-center py-8" data-testid="quick-quote-success">
            <CheckCircle2 className="text-primary mx-auto mb-3" size={40} />
            <h3 className="font-heading font-bold text-xl text-secondary">Quotation request received!</h3>
            <p className="mt-2 text-sm text-muted-foreground">Our team will review your requirement and get back to you shortly.</p>
            <Button onClick={handleClose} className="mt-5 rounded-sm bg-primary hover:bg-primary/90" data-testid="quick-quote-close-btn">Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-secondary font-heading">Request a Quotation</DialogTitle>
              <DialogDescription>Share your requirement — we'll prepare a quotation for your review.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="quick-quote-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label htmlFor="qq-name">Name *</Label><Input id="qq-name" value={form.name} onChange={set("name")} className="mt-1.5 rounded-sm" data-testid="quick-quote-name" /></div>
                <div><Label htmlFor="qq-org">Organization</Label><Input id="qq-org" value={form.organization} onChange={set("organization")} className="mt-1.5 rounded-sm" data-testid="quick-quote-organization" /></div>
                <div><Label htmlFor="qq-phone">Phone *</Label><Input id="qq-phone" type="tel" value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-sm" data-testid="quick-quote-phone" /></div>
                <div><Label htmlFor="qq-email">Email</Label><Input id="qq-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5 rounded-sm" data-testid="quick-quote-email" /></div>
                <div><Label htmlFor="qq-location">Project Location</Label><Input id="qq-location" value={form.location} onChange={set("location")} className="mt-1.5 rounded-sm" data-testid="quick-quote-location" /></div>
                <div><Label htmlFor="qq-building">Building / Project Type</Label><Input id="qq-building" value={form.building_type} onChange={set("building_type")} className="mt-1.5 rounded-sm" data-testid="quick-quote-building" /></div>
              </div>
              <div><Label htmlFor="qq-service">Service Required</Label><Input id="qq-service" placeholder="e.g. Fire Alarm, Sprinkler, AMC" value={form.service} onChange={set("service")} className="mt-1.5 rounded-sm" data-testid="quick-quote-service" /></div>
              <div><Label htmlFor="qq-message">Brief Requirement / Message</Label><Textarea id="qq-message" rows={3} value={form.message} onChange={set("message")} className="mt-1.5 rounded-sm" data-testid="quick-quote-message" /></div>
              {error && <p className="text-sm text-primary" data-testid="quick-quote-error">{error}</p>}
              <Button type="submit" disabled={submitting} className="w-full rounded-sm bg-primary hover:bg-primary/90 font-semibold gap-2" data-testid="quick-quote-submit-btn">
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : "SUBMIT QUOTE REQUEST"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function LeadModalProvider({ children }) {
  const [modal, setModal] = useState(null);
  const [modalContext, setModalContext] = useState(null);
  // `context` (e.g. { service_area: "West Tripura" }) is only ever passed explicitly by the
  // caller (district pages) — never guessed here. Cleared whenever a modal closes.
  // Guard: several call sites pass the handler straight to onClick (e.g. onClick={openQuote}),
  // which would hand us a React SyntheticEvent. Only accept a plain object context.
  const safeContext = (context) =>
    context && typeof context === "object" && !context.nativeEvent && !context.target ? context : null;
  const value = {
    openQuote: (context) => { setModal("quote"); setModalContext(safeContext(context)); },
    openSiteVisit: (context) => { setModal("site_visit"); setModalContext(safeContext(context)); },
  };
  const closeModal = () => { setModal(null); setModalContext(null); };
  return (
    <LeadModalContext.Provider value={value}>
      {children}
      <QuickQuoteModal open={modal === "quote"} onClose={closeModal} context={modalContext} />
      <QuickSiteVisitModal open={modal === "site_visit"} onClose={closeModal} context={modalContext} />
    </LeadModalContext.Provider>
  );
}

/* ---------------- Small building blocks ---------------- */
export function SectionTag({ children, light }) {
  return (
    <p className={`text-xs uppercase tracking-[0.25em] font-semibold mb-3 flex items-center gap-2.5 ${light ? "text-gold" : "text-primary"}`}>
      <span className="divider-gold" />{children}
    </p>
  );
}

export function Breadcrumbs({ items }) {
  // items: [{ label, to? }] — last item has no `to` (current page).
  // Rendered on light/white page sections, so uses dark-on-light tokens (not the gold-on-navy
  // styling used for hero/dark sections) to meet WCAG AA contrast.
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap" data-testid="breadcrumbs">
      <Link to="/" className="hover:text-primary transition-colors">Home</Link>
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1.5">
          <ChevronRight size={12} className="text-muted-foreground/60" />
          {it.to ? <Link to={it.to} className="hover:text-primary transition-colors">{it.label}</Link> : <span className="text-secondary font-medium">{it.label}</span>}
        </span>
      ))}
    </nav>
  );
}

const NAV_LINKS = [
  ["/", "Home"],
  ["/about", "About Us"],
  ["/services", "Services"],
  ["/our-work", "Our Work"],
  ["/industries", "Industries"],
  ["/fire-noc-support", "Fire NOC Support"],
  ["/amc", "AMC"],
  ["/careers", "Careers"],
  ["/contact", "Contact"],
];

function ServicesDropdown() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link to="/services" className="text-sm text-slate-300 hover:text-white transition-colors whitespace-nowrap flex items-center gap-1" data-testid="nav-services">
        Services <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </Link>
      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-64" data-testid="services-dropdown">
          <div className="bg-[#0d2547] border border-white/10 rounded-sm shadow-2xl py-2 overflow-hidden">
            <Link to="/services" className="block px-4 py-2.5 text-sm font-semibold text-gold hover:bg-white/5 border-b border-white/10">All Services</Link>
            {SERVICE_PAGES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors" data-testid={`nav-services-${s.slug}`}>
                <s.icon size={15} className="text-primary shrink-0" /> {s.title}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PublicNav() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const { openQuote } = useLeadModal();
  useEffect(() => { setOpen(false); setServicesOpen(false); }, [location.pathname]);

  return (
    <>
      <div className="hidden xl:flex items-center justify-between bg-[#081A33] text-slate-300 text-xs px-6 py-1.5 border-b border-white/5">
        <div className="flex items-center gap-5">
          <a href={`tel:${PHONE_PRIMARY_TEL}`} className="flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="topbar-phone"><Phone size={12} /> {PHONE_PRIMARY}</a>
          <a href={`mailto:${COMPANY_EMAIL}`} className="flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="topbar-email"><Mail size={12} /> {COMPANY_EMAIL}</a>
        </div>
        <Link to="/login" className="hover:text-gold transition-colors" data-testid="topbar-login">Client Portal</Link>
      </div>
      <header className="sticky top-0 z-50 bg-[#081A33]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[68px] xl:h-[76px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 text-white shrink-0" data-testid="brand-logo">
            <Flame className="text-primary shrink-0" size={28} fill="currentColor" />
            <span className="font-heading tracking-tight leading-none">
              <span className="block font-black text-lg sm:text-xl xl:text-2xl">SECUREFLAME</span>
              <span className="hidden sm:block text-[10px] xl:text-xs uppercase tracking-[0.14em] text-gold font-medium">Fire Systems Private Limited</span>
            </span>
          </Link>
          <nav className="hidden xl:flex items-center gap-5 shrink-0">
            {NAV_LINKS.map(([to, label]) => (
              label === "Services"
                ? <ServicesDropdown key={to} />
                : <Link key={to} to={to} className="text-sm text-slate-300 hover:text-white transition-colors whitespace-nowrap" data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>{label}</Link>
            ))}
          </nav>
          <div className="hidden xl:block shrink-0">
            <Button onClick={() => openQuote()} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold tracking-wide whitespace-nowrap" data-testid="nav-get-quote-btn">GET A QUOTE</Button>
          </div>
          <button onClick={() => setOpen((v) => !v)} className="xl:hidden text-white p-1 -mr-1" aria-label="Menu" data-testid="mobile-menu-btn">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
        {open && (
          <div className="xl:hidden absolute top-full left-0 right-0 bg-[#081A33] border-b border-white/10 px-6 py-3 flex flex-col animate-fade-up max-h-[calc(100vh-68px)] overflow-y-auto" data-testid="mobile-menu">
            {NAV_LINKS.map(([to, label]) => (
              label === "Services" ? (
                <div key={to} className="border-b border-white/5">
                  <button onClick={() => setServicesOpen((v) => !v)} className="w-full flex items-center justify-between py-3 text-slate-200 hover:text-white text-sm" data-testid="mobile-nav-services-toggle">
                    Services <ChevronDown size={16} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {servicesOpen && (
                    <div className="pb-2 pl-3 flex flex-col" data-testid="mobile-services-accordion">
                      <Link to="/services" onClick={() => setOpen(false)} className="py-2 text-sm font-semibold text-gold">All Services</Link>
                      {SERVICE_PAGES.map((s) => (
                        <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)} className="py-2 text-sm text-slate-300" data-testid={`mobile-nav-services-${s.slug}`}>{s.title}</Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={to} to={to} onClick={() => setOpen(false)} className="py-3 text-slate-200 hover:text-white border-b border-white/5 text-sm" data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>{label}</Link>
              )
            ))}
            <Link to="/login" onClick={() => setOpen(false)} className="py-3 text-slate-400 text-sm" data-testid="mobile-nav-login">Client Portal</Link>
            <Button onClick={() => { setOpen(false); openQuote(); }} className="w-full mt-3 rounded-sm bg-primary hover:bg-primary/90 font-semibold" data-testid="mobile-nav-get-quote-btn">GET A QUOTE</Button>
          </div>
        )}
      </header>
    </>
  );
}

export function StickyMobileBar() {
  const { openQuote } = useLeadModal();
  return (
    <div className="xl:hidden fixed bottom-0 left-0 right-0 z-40 grid grid-cols-3 bg-[#081A33] border-t border-white/10 shadow-[0_-4px_20px_rgba(0,0,0,0.25)]" data-testid="sticky-mobile-bar">
      <a href={`tel:${PHONE_PRIMARY_TEL}`} className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-white active:bg-white/10" data-testid="sticky-call-btn">
        <Phone size={18} /><span className="text-[11px] font-semibold tracking-wide">CALL</span>
      </a>
      <a href={waLink()} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-white bg-[#128C4A] active:bg-[#0f7a3f]" data-testid="sticky-whatsapp-btn">
        <WhatsAppIcon size={18} /><span className="text-[11px] font-semibold tracking-wide">WHATSAPP</span>
      </a>
      <button onClick={() => openQuote()} className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-white bg-primary active:bg-primary/80" data-testid="sticky-quote-btn">
        <ClipboardList size={18} /><span className="text-[11px] font-semibold tracking-wide">GET QUOTE</span>
      </button>
    </div>
  );
}

export function SiteLayout({ children }) {
  useEffect(() => {
    if (!document.getElementById("sf-org-jsonld")) {
      const script = document.createElement("script");
      script.id = "sf-org-jsonld";
      script.type = "application/ld+json";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: COMPANY_NAME,
        telephone: PHONE_PRIMARY,
        email: COMPANY_EMAIL,
        address: { "@type": "PostalAddress", streetAddress: COMPANY_ADDRESS, addressRegion: "Tripura", addressCountry: "IN" },
        areaServed: DISTRICTS,
        url: SITE_ORIGIN,
      });
      document.head.appendChild(script);
    }
  }, []);
  return (
    <div className="min-h-screen bg-background">
      <PublicNav />
      {children}
      <Footer />
      <StickyMobileBar />
    </div>
  );
}

export function PageHero({ eyebrow, title, subtitle }) {
  return (
    <section className="relative blueprint-bg text-white overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#081A33]/40 to-[#081A33]" />
      <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
        <SectionTag light>{eyebrow}</SectionTag>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-[1.05]">{title}</h1>
        {subtitle && <p className="mt-5 text-slate-300 max-w-2xl text-base sm:text-lg">{subtitle}</p>}
      </div>
    </section>
  );
}
export function Footer() {
  return (
    <footer className="bg-[#081A33] text-slate-400 pb-16 lg:pb-0">
      <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="md:col-span-2 lg:col-span-2">
          <div className="flex items-center gap-2.5 text-white mb-4">
            <Flame className="text-primary" size={24} fill="currentColor" />
            <span className="font-heading font-black text-lg">SECUREFLAME</span>
          </div>
          <p className="text-sm max-w-md leading-relaxed">SecureFlame Fire Systems Private Limited — complete fire protection engineering: design, supply, installation, testing, commissioning and AMC services across Tripura.</p>
          <div className="mt-5 divider-gold" />
        </div>
        <div className="text-sm space-y-3">
          <p className="text-white font-semibold mb-1 uppercase text-xs tracking-widest">Contact</p>
          <a href={`tel:${PHONE_PRIMARY_TEL}`} className="flex items-start gap-2 hover:text-white transition-colors"><Phone size={15} className="mt-0.5 shrink-0" /> {PHONE_PRIMARY}</a>
          <a href={`mailto:${COMPANY_EMAIL}`} className="flex items-start gap-2 hover:text-white transition-colors"><Mail size={15} className="mt-0.5 shrink-0" /> {COMPANY_EMAIL}</a>
          <p className="flex items-start gap-2"><MapPin size={15} className="mt-0.5 shrink-0" /> {COMPANY_ADDRESS}</p>
        </div>
        <div className="text-sm">
          <p className="text-white font-semibold mb-2 uppercase text-xs tracking-widest">Company</p>
          {NAV_LINKS.slice(1).map(([to, label]) => <Link key={to} to={to} className="block py-1 hover:text-white transition-colors">{label}</Link>)}
          <Link to="/login" className="block py-1 hover:text-white transition-colors">Client Portal</Link>
        </div>
        <div className="text-sm">
          <p className="text-white font-semibold mb-2 uppercase text-xs tracking-widest">Services</p>
          {SERVICE_PAGES.map((s) => <Link key={s.slug} to={`/services/${s.slug}`} className="block py-1 hover:text-white transition-colors">{s.title}</Link>)}
          <Link to="/service-areas" className="block py-1 mt-2 text-gold hover:text-white transition-colors">Service Areas ›</Link>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs px-6">© {new Date().getFullYear()} SecureFlame Fire Systems Private Limited. All rights reserved. Statutory fire safety approvals are issued solely by the relevant government authority.</div>
    </footer>
  );
}
